import { useSignUpForm } from "../hooks/useSignUpForm";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import useSupportedCountries from "src/utils/hooks/useSupportedCountries";
import { isUkCountry } from "src/utils/isUkCountry";
import CountryFlag from "src/utils/CountryFlag";
import DropdownIcon from "../icons/DropdownIcon";
import EnvelopeIcon from "../icons/EnvelopeIcon";
import EyeClosedIcon from "../icons/EyeClosedIcon";
import EyeOpenIcon from "../icons/EyeOpenIcon";
import ProfileIcon from "../icons/ProfileIcon";
import { trackEvent } from "src/Amplitude";
import StylisedButton, { StylisedButtonStyle } from "./StylisedButton";
import StylisedInputField from "./StylisedInputField";
import {
  FormWrapper,
  FormFrame,
  FormHeader,
  FormContent,
  FormHeaderTextWrapper,
  FormHeaderText,
  FormHeaderButton,
  FormContentTitle,
  FormContentFields,
  FormContentTitleAndFields,
  FormContentFieldsFooter,
  FormContentFieldsFooterText,
  FormContentFieldsFooterTextUnderline,
  FormHeaderTextMobile,
  FormFrameMobile,
  FormContentTitleAndFieldsMobile,
  FormCallsToActionWrapper,
  FormContentNameFieldsWrapper,
  FormContentInputFieldWrapperFlex,
  FormContentInputFieldWrapperStretch,
  FormContentCountryFieldWrapper,
  FormContentCountryFieldWrapperMobile,
  FormContentInputDropdownOptionsWrapper,
  FormContentInputDropdown,
  FormContentInputDropdownOption,
  FormContentInputDropdownOptionText,
  FormContentInputFieldError,
  FormContentInputFieldErrorText,
  FormContentPasswordFieldValidationField,
  FormContentPasswordFieldValidationWrapper,
  FormContentPasswordFieldValidationWrapperMobile,
  FormSubscriptionHeaderWrapper,
  FormSubscriptionHeaderContent,
  FormSubscriptionHeaderTextWrapper,
  FormSubscriptionInputWrapper,
  FormSubscriptionInputTitle,
  FormSubscriptionOptionsWrapper,
  FormSubscriptionOption,
  FormSubscriptionOptionTextWrapper,
  FormSubscriptionOptionSubtextWrapper,
  FormSubscriptionOptionSubtext,
  FormSubscriptionOptionSubtextMobile,
  FormSubscriptionInputTitleMobile,
  FormSubscriptionOptionTextWrapperMobile,
  FormReferralDisclosureText,
  FormReferralDisclosureWrapper,
  FormReferralDisclosureTextWrapper,
  FormPhoneValidationHeaderText,
  FormPhoneValidationTextAndInputWrapper,
  FormSubscriptionHeaderTextMobile,
  FormSubscriptionHeaderSubTextMobile,
  FormSubscriptionHeaderText,
  FormSubscriptionHeaderSubText,
  FormUnsupportedCountryHeaderText,
  FormUnsupportedCountryHeaderSubText,
  FormUnsupportedCountryHeaderWrapper,
} from "../styles/LandingPageForm.styles";
import React, { useEffect } from "react";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import {
  PHONE_VALIDATION_STAGE,
  SIGN_UP_STAGE,
  SUBSCRIPTION_STAGE,
  useSignUpFormSlice,
} from "src/store/features/signUpForm.slice";
import { CircularProgress, Link } from "@mui/material";
import { US_STATES } from "../constants/states";
import ErrorIcon from "../icons/ErrorIcon";
import PasswordValidationValidIcon from "../icons/PasswordValidationValidIcon";
import PasswordValidationInvalidIcon from "../icons/PasswordValidationInvalidIcon";
import { useAppDispatch } from "src/store/store";
import {
  setSignupSources,
  SubscribeGroupType,
  useGetSubscribedGroups,
  useSubscribeState,
} from "src/store/features/subscribe.slice";
import RadioUnselectedIcon from "../icons/RadioUnselectedIcon";
import RadioSelectedIcon from "../icons/RadioSelectedIcon";
import { useSignUpFormInputSlice } from "src/store/features/signUpFormInput.slice";
import { getLandingPageReferralDisclosureMessage } from "src/utils/referral-partner-disclosure";
import {
  CountryIso2,
  defaultCountries,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import TurnstileWidget from "./TurnstileWidget";
import { DisclosureFlow } from "src/components/disclosures/DisclosureFlow";
import { useTurnstileSlice, resetTurnstile } from "src/store/features/turnstile.slice";

const FormHeaderComponent = () => {
  const isMobile = useIsMobile();

  const HeaderButton = () => {
    return (
      <FormHeaderButton
        onClick={() => {
          window.location.href = "/login";
        }}>
        Login
      </FormHeaderButton>
    );
  };

  return isMobile ? (
    <FormHeader>
      <FormHeaderTextWrapper
        isMobile={true}
        onClick={() => window.open("https://calebandbrown.com/", "_blank")}>
        <FormHeaderTextMobile>
          <CalebAndBrownLogo
            height={40}
            width={141}
            color="currentColor"
            aria-label="Caleb & Brown"
          />
        </FormHeaderTextMobile>
      </FormHeaderTextWrapper>
      <HeaderButton />
    </FormHeader>
  ) : (
    <FormHeader>
      <FormHeaderTextWrapper
        isMobile={false}
        onClick={() => window.open("https://calebandbrown.com/", "_blank")}>
        <FormHeaderText>
          <CalebAndBrownLogo
            height={40}
            width={141}
            color="currentColor"
            aria-label="Caleb & Brown"
          />
        </FormHeaderText>
      </FormHeaderTextWrapper>
      <HeaderButton />
    </FormHeader>
  );
};

const FormCallToActionComponent = () => {
  const { isLoadingSubmitPending, submitErrorMessage } = useSignUpFormInputSlice();
  const { handleSubmit } = useSignUpForm();

  return (
    <FormCallsToActionWrapper>
      <StylisedButton
        isLoading={isLoadingSubmitPending}
        isLoadingContents={<CircularProgress size={16} sx={{ color: "#FC4C4C" }} />}
        isDisabled={isLoadingSubmitPending}
        onClick={() => handleSubmit()}
        style={StylisedButtonStyle.SALMON}
        buttonText={"Create account"}
      />

      {/* COMMENTED OUT FOR NOW UNTIL GOOGLE AUTH IS ENABLED */}
      {/* <StylisedButton
        isLoading={false}
        isDisabled={isLoadingSubmitPending}
        onClick={handleSubmit}
        style={StylisedButtonStyle.OUTLINE}
        buttonStartAdornment={
          <FormGoogleButtonIconWrapper>
            <GoogleIcon />
          </FormGoogleButtonIconWrapper>
        }
        buttonText={"Continue with Google"}
      /> */}

      {!!submitErrorMessage && (
        <FormContentInputFieldError>
          <ErrorIcon />
          <FormContentInputFieldErrorText>{submitErrorMessage}</FormContentInputFieldErrorText>
        </FormContentInputFieldError>
      )}
    </FormCallsToActionWrapper>
  );
};

interface CountryDropdownProps {
  countries: Array<{ value: string; label: string }>;
  onCountrySelect: (country: { value: string; label: string }) => void;
}
const CountryDropdown = ({ countries, onCountrySelect }: CountryDropdownProps) => {
  return (
    <FormContentInputDropdown>
      <FormContentInputDropdownOptionsWrapper>
        {countries.map((c, index) => (
          <FormContentInputDropdownOption
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent input from losing focus
              onCountrySelect(c);
            }}
            key={index}>
            <CountryFlag countrycode={c.value.toLowerCase()} size={20} />
            <FormContentInputDropdownOptionText>{c.label}</FormContentInputDropdownOptionText>
          </FormContentInputDropdownOption>
        ))}
        {countries.length === 0 && (
          <FormContentInputDropdownOption key={0}>
            <FormContentInputDropdownOptionText>
              No countries found
            </FormContentInputDropdownOptionText>
          </FormContentInputDropdownOption>
        )}
      </FormContentInputDropdownOptionsWrapper>
    </FormContentInputDropdown>
  );
};
const CountryField = () => {
  const { allCountries } = useSupportedCountries();
  const { handleCountrySearchChange, handleCountryChange, toggleCountryDropdown } = useSignUpForm();
  const { countrySearch, country, countryError, isCountryDropdownOpen, isLoadingSubmitPending } =
    useSignUpFormInputSlice();

  const SelectedCountryIcon = () => {
    return country ? (
      <CountryFlag
        countrycode={allCountries.find((c) => c.label === country)?.value.toLowerCase() || ""}
        size={20}
      />
    ) : (
      <></>
    );
  };

  const filteredCountries = allCountries.filter((c) => {
    const searchTerm = countrySearch.toLowerCase();
    const countryLabel = c.label.toLowerCase();

    // Normal search
    if (countryLabel.includes(searchTerm)) {
      return true;
    }

    // Special cases for common abbreviations
    if (searchTerm === "us" || searchTerm === "usa") {
      return countryLabel === "united states";
    }

    if (searchTerm === "uk") {
      return countryLabel === "united kingdom";
    }

    return false;
  });

  const handleCountrySelectFromDropdown = (selectedCountry: { value: string; label: string }) => {
    // Update form state through the hook
    const syntheticEvent = {
      target: {
        value: selectedCountry.label,
        selectionStart: 0,
        selectionEnd: 0,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    handleCountrySearchChange(syntheticEvent);
    handleCountryChange(selectedCountry.label);

    toggleCountryDropdown(false);
  };

  return (
    <>
      <StylisedInputField
        value={countrySearch}
        onChange={handleCountrySearchChange}
        isDisabled={isLoadingSubmitPending}
        placeholder="Select country"
        captionText="Country of residence"
        startAdornment={<SelectedCountryIcon />}
        endAdornment={<DropdownIcon />}
        errorMsg={countryError}
        onBlur={() => {
          toggleCountryDropdown(false);
        }}
        onClick={() => {
          toggleCountryDropdown(!isCountryDropdownOpen);
        }}
      />
      {isCountryDropdownOpen && (
        <CountryDropdown
          countries={filteredCountries}
          onCountrySelect={handleCountrySelectFromDropdown}
        />
      )}
    </>
  );
};

interface StateDropdownProps {
  states: Array<{ value: string; label: string }>;
  onStateSelect: (state: { value: string; label: string }) => void;
}
const StateDropdown = ({ states, onStateSelect }: StateDropdownProps) => {
  return (
    <FormContentInputDropdown>
      <FormContentInputDropdownOptionsWrapper>
        {states.map((s, index) => (
          <FormContentInputDropdownOption
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent input from losing focus
              onStateSelect(s);
            }}
            key={index}>
            <FormContentInputDropdownOptionText>{s.label}</FormContentInputDropdownOptionText>
          </FormContentInputDropdownOption>
        ))}
        {states.length === 0 && (
          <FormContentInputDropdownOption key={0}>
            <FormContentInputDropdownOptionText>No states found</FormContentInputDropdownOptionText>
          </FormContentInputDropdownOption>
        )}
      </FormContentInputDropdownOptionsWrapper>
    </FormContentInputDropdown>
  );
};
const StateField = () => {
  const { handleStateSearchChange, handleStateChange, toggleStateDropdown } = useSignUpForm();
  const { stateSearch, stateError, isStateDropdownOpen, isLoadingSubmitPending } =
    useSignUpFormInputSlice();

  const filteredStates = US_STATES.filter((s) =>
    s.toLowerCase().includes(stateSearch.toLowerCase()),
  );

  const handleStateSelectFromDropdown = (selectedState: { value: string; label: string }) => {
    // Update form state through the hook
    const syntheticEvent = {
      target: {
        value: selectedState.label,
        selectionStart: 0,
        selectionEnd: 0,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    handleStateSearchChange(syntheticEvent);
    handleStateChange(selectedState.label);

    toggleStateDropdown(false);
  };

  return (
    <>
      <StylisedInputField
        value={stateSearch}
        onChange={handleStateSearchChange}
        isDisabled={isLoadingSubmitPending}
        placeholder="Select state"
        captionText="State"
        endAdornment={<DropdownIcon />}
        errorMsg={stateError}
        onBlur={() => {
          toggleStateDropdown(false);
        }}
        onClick={() => {
          toggleStateDropdown(!isStateDropdownOpen);
        }}
      />
      {isStateDropdownOpen && (
        <StateDropdown
          states={filteredStates.map((s) => ({ value: s, label: s }))}
          onStateSelect={handleStateSelectFromDropdown}
        />
      )}
    </>
  );
};

const FormCountryFieldComponent = () => {
  const isMobile = useIsMobile();
  const { country } = useSignUpFormInputSlice();

  return isMobile ? (
    <FormContentCountryFieldWrapperMobile>
      <FormContentInputFieldWrapperStretch>
        <CountryField />
      </FormContentInputFieldWrapperStretch>
      {country === "United States" && (
        <FormContentInputFieldWrapperStretch>
          <StateField />
        </FormContentInputFieldWrapperStretch>
      )}
    </FormContentCountryFieldWrapperMobile>
  ) : (
    <FormContentCountryFieldWrapper>
      <FormContentInputFieldWrapperFlex>
        <CountryField />
      </FormContentInputFieldWrapperFlex>
      {country === "United States" && (
        <FormContentInputFieldWrapperFlex>
          <StateField />
        </FormContentInputFieldWrapperFlex>
      )}
    </FormContentCountryFieldWrapper>
  );
};

const FormNameFieldsComponent = () => {
  const isMobile = useIsMobile();
  const { handleFirstNameChange, handleLastNameChange } = useSignUpForm();
  const { firstName, firstNameError, lastName, lastNameError, isLoadingSubmitPending } =
    useSignUpFormInputSlice();

  return isMobile ? (
    <>
      <FormContentInputFieldWrapperStretch>
        <StylisedInputField
          value={firstName}
          onChange={handleFirstNameChange}
          isDisabled={isLoadingSubmitPending}
          placeholder="Legal first name"
          captionText="First Name"
          endAdornment={<ProfileIcon />}
          errorMsg={firstNameError}
        />
      </FormContentInputFieldWrapperStretch>
      <FormContentInputFieldWrapperStretch>
        <StylisedInputField
          value={lastName}
          onChange={handleLastNameChange}
          isDisabled={isLoadingSubmitPending}
          placeholder="Legal last name"
          captionText="Last Name"
          endAdornment={<ProfileIcon />}
          errorMsg={lastNameError}
        />
      </FormContentInputFieldWrapperStretch>
    </>
  ) : (
    <FormContentNameFieldsWrapper>
      <FormContentInputFieldWrapperFlex>
        <StylisedInputField
          value={firstName}
          onChange={handleFirstNameChange}
          isDisabled={isLoadingSubmitPending}
          placeholder="Legal first name"
          captionText="First Name"
          endAdornment={<ProfileIcon />}
          errorMsg={firstNameError}
        />
      </FormContentInputFieldWrapperFlex>
      <FormContentInputFieldWrapperFlex>
        <StylisedInputField
          value={lastName}
          onChange={handleLastNameChange}
          isDisabled={isLoadingSubmitPending}
          placeholder="Legal last name"
          captionText="Last Name"
          endAdornment={<ProfileIcon />}
          errorMsg={lastNameError}
        />
      </FormContentInputFieldWrapperFlex>
    </FormContentNameFieldsWrapper>
  );
};

const FormPasswordFieldComponent = () => {
  const isMobile = useIsMobile();
  const { handlePasswordChange, togglePasswordVisibility, getPasswordValidationResults } =
    useSignUpForm();
  const { password, passwordError, isPasswordVisible, isLoadingSubmitPending } =
    useSignUpFormInputSlice();

  const validationResults = getPasswordValidationResults(password);

  const ValidationFields = () => {
    return (
      <>
        <FormContentPasswordFieldValidationField>
          {validationResults.hasLowercaseOrUppercase ? (
            <PasswordValidationValidIcon />
          ) : (
            <PasswordValidationInvalidIcon />
          )}
          <FormContentInputFieldErrorText>
            1 Lowercase & uppercase letter
          </FormContentInputFieldErrorText>
        </FormContentPasswordFieldValidationField>
        <FormContentPasswordFieldValidationField>
          {validationResults.has8Characters ? (
            <PasswordValidationValidIcon />
          ) : (
            <PasswordValidationInvalidIcon />
          )}
          <FormContentInputFieldErrorText>8 characters</FormContentInputFieldErrorText>
        </FormContentPasswordFieldValidationField>
        <FormContentPasswordFieldValidationField>
          {validationResults.hasSpecialCharacter ? (
            <PasswordValidationValidIcon />
          ) : (
            <PasswordValidationInvalidIcon />
          )}
          <FormContentInputFieldErrorText>1 Special character</FormContentInputFieldErrorText>
        </FormContentPasswordFieldValidationField>
        <FormContentPasswordFieldValidationField>
          {validationResults.hasNumber ? (
            <PasswordValidationValidIcon />
          ) : (
            <PasswordValidationInvalidIcon />
          )}
          <FormContentInputFieldErrorText>1 Number</FormContentInputFieldErrorText>
        </FormContentPasswordFieldValidationField>
      </>
    );
  };

  const Validation = () => {
    return isMobile ? (
      <FormContentPasswordFieldValidationWrapperMobile>
        <ValidationFields />
      </FormContentPasswordFieldValidationWrapperMobile>
    ) : (
      <FormContentPasswordFieldValidationWrapper>
        <ValidationFields />
      </FormContentPasswordFieldValidationWrapper>
    );
  };

  return (
    <FormContentInputFieldWrapperStretch>
      <StylisedInputField
        value={password}
        onChange={handlePasswordChange}
        isDisabled={isLoadingSubmitPending}
        placeholder="Password"
        captionText="Password"
        endAdornment={
          <div
            onClick={togglePasswordVisibility}
            style={{ cursor: "pointer" }}
            className="password-toggle-icon">
            {isPasswordVisible ? <EyeOpenIcon /> : <EyeClosedIcon />}
          </div>
        }
        errorMsg={passwordError}
        forceError={!!password && !validationResults.allValid}
        type={isPasswordVisible ? "text" : "password"}
      />
      {!!password && !passwordError && <Validation />}
    </FormContentInputFieldWrapperStretch>
  );
};

const FormFieldsComponent = () => {
  const { handleEmailChange, handleConfirmPasswordChange, toggleConfirmPasswordVisibility } =
    useSignUpForm();
  const {
    email,
    emailError,
    confirmPassword,
    confirmPasswordError,
    isConfirmPasswordVisible,
    isLoadingSubmitPending,
  } = useSignUpFormInputSlice();

  return (
    <FormContentFields>
      {/* COUNTRY */}
      <FormCountryFieldComponent />
      {/* NAMES */}
      <FormNameFieldsComponent />
      {/* EMAIL */}
      <FormContentInputFieldWrapperStretch>
        <StylisedInputField
          value={email}
          onChange={handleEmailChange}
          isDisabled={isLoadingSubmitPending}
          placeholder="Email address"
          captionText="Email Address"
          endAdornment={<EnvelopeIcon />}
          errorMsg={emailError}
        />
      </FormContentInputFieldWrapperStretch>
      {/* PASSWORD */}
      <FormPasswordFieldComponent />
      {/* CONFIRM PASSWORD */}
      <FormContentInputFieldWrapperStretch>
        <StylisedInputField
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          isDisabled={isLoadingSubmitPending}
          placeholder="Confirm password"
          captionText="Confirm Password"
          endAdornment={
            <div
              onClick={toggleConfirmPasswordVisibility}
              style={{ cursor: "pointer" }}
              className="password-toggle-icon">
              {isConfirmPasswordVisible ? <EyeOpenIcon /> : <EyeClosedIcon />}
            </div>
          }
          errorMsg={confirmPasswordError}
          type={isConfirmPasswordVisible ? "text" : "password"}
        />
      </FormContentInputFieldWrapperStretch>
    </FormContentFields>
  );
};

const ReferralDisclosure = () => {
  const { referralDetails } = useSignUpFormInputSlice();
  if (!referralDetails || !referralDetails.referrer) return <></>;

  return (
    <FormReferralDisclosureWrapper>
      <FormReferralDisclosureTextWrapper>
        <FormReferralDisclosureText>
          {getLandingPageReferralDisclosureMessage(
            referralDetails.name,
            referralDetails.rewards ?? "",
          )}
        </FormReferralDisclosureText>
      </FormReferralDisclosureTextWrapper>
    </FormReferralDisclosureWrapper>
  );
};

// Must stay at module scope: defining this inside FormInitialInputFormComponent
// creates a new component type on every render, remounting every input and
// dropping focus/clicks each time the sign-up slice updates.
const TitleAndFields = () => {
  const { country } = useSignUpFormInputSlice();
  // UK clients get a slightly different agreement wording.
  const isUk = isUkCountry(country);

  return (
    <>
      <FormContentTitle>Get started</FormContentTitle>
      <ReferralDisclosure />
      <FormFieldsComponent />
      <FormContentFieldsFooter>
        <FormContentFieldsFooterText>
          By <span style={{ color: "#002035" }}>creating an account</span>, I agree that I am{" "}
          <span style={{ color: "#002035" }}>at least 18 years old</span>
          {isUk ? ", agree to" : " and agree to"}{" "}
          <FormContentFieldsFooterTextUnderline isClickable={true}>
            <a
              href={"https://calebandbrown.com/website-terms-of-use/"}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#002035" }}
              onClick={() => trackEvent("User redirected to fund website", undefined, "Sign_Up")}>
              Caleb & Brown's Terms of Business
            </a>
          </FormContentFieldsFooterTextUnderline>
          {isUk ? ", and acknowledge our" : ", and acknowledge their"}{" "}
          <FormContentFieldsFooterTextUnderline isClickable={true}>
            <a
              href={"https://calebandbrown.com/privacy-policy/"}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#002035" }}
              onClick={() => trackEvent("User redirected to fund website", undefined, "Sign_Up")}>
              Privacy Policy
            </a>
          </FormContentFieldsFooterTextUnderline>
          .
        </FormContentFieldsFooterText>
      </FormContentFieldsFooter>
    </>
  );
};

const FormInitialInputFormComponent = () => {
  const isMobile = useIsMobile();

  return isMobile ? (
    <>
      <FormContentTitleAndFieldsMobile>
        <TitleAndFields />
      </FormContentTitleAndFieldsMobile>
      <FormCallToActionComponent />
    </>
  ) : (
    <FormContent>
      <FormContentTitleAndFields>
        <TitleAndFields />
      </FormContentTitleAndFields>
      <FormCallToActionComponent />
    </FormContent>
  );
};

const FormSubscriptionComponent = () => {
  const isMobile = useIsMobile();
  const { handleSubscribeOptionClick, handleSubscribeSubmit, handleSubscribeNotNow } =
    useSignUpForm();
  const { subscriptionStage } = useSignUpFormSlice();
  const { isLoadingSubscribe, email } = useSignUpFormInputSlice();

  const SubscribeState = useSubscribeState();
  const subscribedGroups = useGetSubscribedGroups();

  const Header = () => {
    return isMobile ? (
      <FormSubscriptionHeaderContent>
        <FormSubscriptionHeaderTextWrapper>
          <FormSubscriptionHeaderTextMobile>Activate your account</FormSubscriptionHeaderTextMobile>
        </FormSubscriptionHeaderTextWrapper>
        <FormSubscriptionHeaderTextWrapper>
          <FormSubscriptionHeaderSubTextMobile>
            Please complete verification steps sent to {email ?? "your email"}
          </FormSubscriptionHeaderSubTextMobile>
        </FormSubscriptionHeaderTextWrapper>
      </FormSubscriptionHeaderContent>
    ) : (
      <FormSubscriptionHeaderWrapper>
        <FormSubscriptionHeaderContent>
          <FormSubscriptionHeaderTextWrapper>
            <FormSubscriptionHeaderText>Activate your account</FormSubscriptionHeaderText>
          </FormSubscriptionHeaderTextWrapper>
          <FormSubscriptionHeaderTextWrapper>
            <FormSubscriptionHeaderSubText>
              Please complete verification steps sent to {email ?? "your email"}
            </FormSubscriptionHeaderSubText>
          </FormSubscriptionHeaderTextWrapper>
        </FormSubscriptionHeaderContent>
      </FormSubscriptionHeaderWrapper>
    );
  };

  const subscribeOptions: { group: SubscribeGroupType; label: string; subtext: string }[] = [
    {
      group: "brokerage",
      label: "Brokerage Updates & Offers",
      subtext: "New features, rewards, events, and announcements",
    },
    {
      group: "marketing",
      label: "Market Insights",
      subtext: "Weekly Rollup newsletter and crypto market commentary",
    },
  ];

  const Option = ({
    option,
  }: {
    option: { group: SubscribeGroupType; label: string; subtext: string };
  }) => {
    const isSelected = !!SubscribeState[option.group as keyof typeof SubscribeState];

    return (
      <FormSubscriptionOption
        key={option.group}
        isSelected={isSelected}
        disabled={
          isLoadingSubscribe || subscriptionStage === SUBSCRIPTION_STAGE.SUBSCRIPTION_SUCCESS
        }
        onClick={() => handleSubscribeOptionClick(option.group)}>
        {isMobile ? (
          <FormSubscriptionOptionSubtextWrapper>
            <FormSubscriptionOptionTextWrapperMobile>
              {option.label}
            </FormSubscriptionOptionTextWrapperMobile>
            <FormSubscriptionOptionSubtextMobile>
              {option.subtext}
            </FormSubscriptionOptionSubtextMobile>
          </FormSubscriptionOptionSubtextWrapper>
        ) : (
          <FormSubscriptionOptionSubtextWrapper>
            <FormSubscriptionOptionTextWrapper>{option.label}</FormSubscriptionOptionTextWrapper>
            <FormSubscriptionOptionSubtext>{option.subtext}</FormSubscriptionOptionSubtext>
          </FormSubscriptionOptionSubtextWrapper>
        )}
        {isSelected ? <RadioSelectedIcon /> : <RadioUnselectedIcon />}
      </FormSubscriptionOption>
    );
  };

  const Options = () => {
    return (
      <FormSubscriptionOptionsWrapper>
        {subscribeOptions.map((option) => (
          <Option key={option.group} option={option} />
        ))}
      </FormSubscriptionOptionsWrapper>
    );
  };

  const CallToAction = () => {
    // check to see if any of the available subscribeOptions are selected in subscribedGroups
    const anyOptionsSelected = subscribeOptions.some((option) =>
      subscribedGroups.includes(option.group),
    );

    return (
      <FormCallsToActionWrapper>
        <StylisedButton
          isLoading={isLoadingSubscribe}
          isLoadingContents={<CircularProgress size={16} sx={{ color: "#FC4C4C" }} />}
          isDisabled={isLoadingSubscribe || !anyOptionsSelected}
          onClick={() => handleSubscribeSubmit(subscribedGroups)}
          style={StylisedButtonStyle.SALMON}
          buttonText={"Subscribe"}
        />
        <StylisedButton
          isLoading={false}
          isDisabled={false}
          onClick={handleSubscribeNotNow}
          style={StylisedButtonStyle.OUTLINE}
          buttonText={"Not now"}
        />
      </FormCallsToActionWrapper>
    );
  };

  const SubscriptionInput = () => {
    return isMobile ? (
      <FormSubscriptionInputWrapper>
        <FormSubscriptionInputTitleMobile>
          Stay in the loop with Caleb & Brown.
        </FormSubscriptionInputTitleMobile>
        <Options />
        <CallToAction />
      </FormSubscriptionInputWrapper>
    ) : (
      <FormSubscriptionInputWrapper>
        <FormSubscriptionInputTitle>
          Stay in the loop with Caleb & Brown.
        </FormSubscriptionInputTitle>
        <Options />
        <CallToAction />
      </FormSubscriptionInputWrapper>
    );
  };

  const SubscriptionSuccess = () => {
    return isMobile ? (
      <FormSubscriptionInputWrapper>
        <FormSubscriptionInputTitleMobile>
          Thank you for subscribing.
        </FormSubscriptionInputTitleMobile>
        <Options />
      </FormSubscriptionInputWrapper>
    ) : (
      <FormSubscriptionInputWrapper>
        <FormSubscriptionInputTitle>Thank you for subscribing.</FormSubscriptionInputTitle>
        <Options />
      </FormSubscriptionInputWrapper>
    );
  };

  const Content = () => {
    if (subscriptionStage === SUBSCRIPTION_STAGE.SUBSCRIPTION_SUCCESS) {
      return <SubscriptionSuccess />;
    } else if (subscriptionStage === SUBSCRIPTION_STAGE.SUBSCRIPTION_NOT_NOW) {
      return <></>;
    }
    return <SubscriptionInput />;
  };

  return (
    <FormContent>
      <Header />
      <Content />
    </FormContent>
  );
};

const FormUnsupportedCountryComponent = () => {
  const isMobile = useIsMobile();
  const { country, state } = useSignUpFormInputSlice();
  const { isCountrySupported } = useSupportedCountries();
  const { handleUnsupportedCountryGoBack } = useSignUpForm();

  const unsupportedCountry = !isCountrySupported(country);
  let restrictedLocation: string;
  let redirectUrl: string;
  if (state === "New York") {
    restrictedLocation = "New York";
    redirectUrl = "https://funds.calebandbrown.com/usa";
  } else if (state === "South Dakota") {
    restrictedLocation = "South Dakota";
    redirectUrl = "https://funds.calebandbrown.com/usa";
  }

  const Header = () => {
    return (
      <FormUnsupportedCountryHeaderWrapper>
        <FormUnsupportedCountryHeaderText isMobile={isMobile}>
          Thank you for your interest in Caleb & Brown.
        </FormUnsupportedCountryHeaderText>
        {unsupportedCountry ? (
          <FormUnsupportedCountryHeaderSubText style={{ textAlign: "left", alignSelf: "center" }}>
            Unfortunately, we are not accepting clients in your country at this stage. If you are an
            existing client that is currently out of market (eg, travel), please reach out to your
            Broker or our Support team at support@calebandbrown.com.
          </FormUnsupportedCountryHeaderSubText>
        ) : (
          <FormUnsupportedCountryHeaderSubText>
            Currently, we are unable to service {restrictedLocation} residents through our crypto
            brokerage services. However, if you are an accredited investor, we invite you to explore
            our investment opportunities at Caleb and Brown Asset Management. Please visit{" "}
            {redirectUrl} to learn more
          </FormUnsupportedCountryHeaderSubText>
        )}
      </FormUnsupportedCountryHeaderWrapper>
    );
  };

  const CallToAction = () => {
    return (
      <FormCallsToActionWrapper>
        {!unsupportedCountry && (
          <StylisedButton
            isLoading={false}
            isDisabled={false}
            onClick={() => {
              window.open(redirectUrl, "_blank");
            }}
            style={StylisedButtonStyle.SALMON}
            buttonText={
              isMobile ? "See our Asset Management" : "See Caleb & Brown Asset Management"
            }
          />
        )}
        <StylisedButton
          isLoading={false}
          isDisabled={false}
          onClick={handleUnsupportedCountryGoBack}
          style={StylisedButtonStyle.OUTLINE}
          buttonText={"Go back"}
        />
      </FormCallsToActionWrapper>
    );
  };

  return (
    <FormContent>
      <Header />
      <CallToAction />
    </FormContent>
  );
};

interface PhoneCountryDropdownProps {
  countries: Array<{ value: string; label: string }>;
  onCountrySelect: (country: string) => void;
}
const PhoneCountryDropdown = ({ countries, onCountrySelect }: PhoneCountryDropdownProps) => {
  return (
    <FormContentInputDropdown>
      <FormContentInputDropdownOptionsWrapper>
        {countries.map((c, index) => (
          <FormContentInputDropdownOption
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent input from losing focus
              onCountrySelect(c.value);
            }}
            key={index}>
            <CountryFlag countrycode={c.value.toLowerCase()} size={20} />
            <FormContentInputDropdownOptionText>{c.label}</FormContentInputDropdownOptionText>
          </FormContentInputDropdownOption>
        ))}
        {countries.length === 0 && (
          <FormContentInputDropdownOption key={0}>
            <FormContentInputDropdownOptionText>
              No countries found
            </FormContentInputDropdownOptionText>
          </FormContentInputDropdownOption>
        )}
      </FormContentInputDropdownOptionsWrapper>
    </FormContentInputDropdown>
  );
};
const PhoneInputField = () => {
  const {
    handlePhoneChange,
    handlePhoneCountryChange,
    togglePhoneCountryDropdown,
    handleSubmitPhoneNumber,
  } = useSignUpForm();
  const { phone, phoneError, isLoadingSubmitPending, isPhoneCountryDropdownOpen, phoneCountry } =
    useSignUpFormInputSlice();

  const {
    phone: phoneValue,
    handlePhoneValueChange,
    country,
    setCountry,
  } = usePhoneInput({
    defaultCountry: phoneCountry || "us",
    value: phone,
    countries: defaultCountries,
    onChange: (data) => {
      if (data.phone.length > 3) {
        data.phone = "+" + data.phone.replace(/[^0-9]/g, "");
        // special edge case for Australia, if a customer enters 04 after the +61, it should be +614
        if (data.phone.startsWith("+61") && data.phone[3] === "0") {
          data.phone = "+61" + data.phone.slice(4);
        }
      }
      handlePhoneChange({
        target: { value: data.phone },
      } as React.ChangeEvent<HTMLInputElement>);
    },
  });

  const SelectedCountryIcon = () => {
    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          togglePhoneCountryDropdown(!isPhoneCountryDropdownOpen);
        }}>
        {country ? <CountryFlag countrycode={country.toLowerCase()} size={20} /> : <></>}
        <DropdownIcon />
      </div>
    );
  };

  const handleCountrySelectFromDropdown = (selectedCountry: CountryIso2) => {
    setCountry(selectedCountry);
    handlePhoneCountryChange(selectedCountry);
    togglePhoneCountryDropdown(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmitPhoneNumber();
    }
  };

  return (
    <>
      <StylisedInputField
        value={phoneValue}
        onChange={handlePhoneValueChange}
        onKeyDown={handleKeyDown}
        isDisabled={isLoadingSubmitPending}
        placeholder="Phone number"
        startAdornment={<SelectedCountryIcon />}
        errorMsg={""}
        forceError={!!phoneError}
        onBlur={() => {
          togglePhoneCountryDropdown(false);
        }}
      />
      {isPhoneCountryDropdownOpen && (
        <PhoneCountryDropdown
          countries={defaultCountries.map((c) => {
            const country = parseCountry(c);
            return { value: country.iso2, label: `${country.name} (+${country.dialCode})` };
          })}
          onCountrySelect={handleCountrySelectFromDropdown}
        />
      )}
    </>
  );
};

const FormPhoneValidationComponent = () => {
  const {
    phone,
    phoneVerificationCode,
    phoneVerificationCodeError,
    isLoadingPhoneNumber,
    phoneError,
  } = useSignUpFormInputSlice();
  const {
    handleSubmitPhoneNumber,
    handlePhoneVerificationCodeChange,
    handlePhoneValidationGoBack,
    handleResendPhoneNumber,
    countdownSeconds,
    handlePhoneInputGoBack,
    handleVerifyPhoneNumber,
  } = useSignUpForm();
  const { phoneValidationStage } = useSignUpFormSlice();
  const { turnstileToken } = useTurnstileSlice();
  const dispatch = useAppDispatch();

  // Reset Turnstile when an error occurs that requires resending
  // This ensures users get a fresh token when they need to resend
  useEffect(() => {
    const shouldResetTurnstile =
      (phoneVerificationCodeError &&
        (phoneVerificationCodeError.includes("resend code here") ||
          phoneVerificationCodeError.toLowerCase().includes("security verification"))) ||
      (phoneError && phoneError.toLowerCase().includes("security verification"));

    if (shouldResetTurnstile) {
      dispatch(resetTurnstile());
    }
  }, [phoneVerificationCodeError, phoneError, dispatch]);

  const renderPhoneVerificationError = () => {
    if (!phoneVerificationCodeError) {
      return (
        <FormContentInputFieldError>
          <FormContentInputFieldErrorText>
            Didn't receive a code?{" "}
            <span
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "#002035",
              }}
              onClick={handleResendPhoneNumber}>
              Click here to resend
            </span>
          </FormContentInputFieldErrorText>
        </FormContentInputFieldError>
      );
    }

    if (phoneVerificationCodeError.includes("resend code here")) {
      const parts = phoneVerificationCodeError.split("resend code here");
      return (
        <FormContentInputFieldError>
          <ErrorIcon />
          <FormContentInputFieldErrorText>
            {parts[0]}
            <span
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "#002035",
              }}
              onClick={handleResendPhoneNumber}>
              resend code here
            </span>
            {parts[1]}
          </FormContentInputFieldErrorText>
        </FormContentInputFieldError>
      );
    }

    return (
      <FormContentInputFieldError>
        <ErrorIcon />
        <FormContentInputFieldErrorText>
          {phoneVerificationCodeError}
        </FormContentInputFieldErrorText>
      </FormContentInputFieldError>
    );
  };

  const renderPhoneError = () => {
    if (!phoneError) return null;

    if (phoneError.includes("Too many phone number submissions")) {
      const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
          .toString()
          .padStart(2, "0")}`;
      };

      return (
        <FormContentInputFieldError>
          <ErrorIcon />
          <FormContentInputFieldErrorText>
            {phoneError}
            {countdownSeconds !== null && countdownSeconds > 0 && (
              <span style={{ fontWeight: "600", color: "#002035" }}>
                {" "}
                ({formatTime(countdownSeconds)} remaining)
              </span>
            )}
          </FormContentInputFieldErrorText>
        </FormContentInputFieldError>
      );
    }

    return (
      <FormContentInputFieldError>
        <ErrorIcon />
        <FormContentInputFieldErrorText>{phoneError}</FormContentInputFieldErrorText>
      </FormContentInputFieldError>
    );
  };

  const handleVerifyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleVerifyPhoneNumber();
    }
  };

  if (
    phoneValidationStage === PHONE_VALIDATION_STAGE.CODE_SENT ||
    phoneValidationStage === PHONE_VALIDATION_STAGE.CODE_RESENT
  ) {
    return (
      <FormContent>
        <FormPhoneValidationTextAndInputWrapper>
          <FormPhoneValidationHeaderText>
            {phoneValidationStage === PHONE_VALIDATION_STAGE.CODE_SENT
              ? `We've sent you a 6 digit verification code to your phone number ending in ${phone?.slice(
                  -4,
                )}.`
              : "We've sent you a new verification code."}
          </FormPhoneValidationHeaderText>
          <FormContentInputFieldWrapperStretch>
            <StylisedInputField
              value={phoneVerificationCode}
              onChange={handlePhoneVerificationCodeChange}
              onKeyDown={handleVerifyKeyDown}
              isDisabled={isLoadingPhoneNumber}
              placeholder="Enter code here"
              errorMsg={""}
              forceError={!!phoneVerificationCodeError}
              endAdornment={
                isLoadingPhoneNumber ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: "#0BEC9F" }} />
                ) : (
                  <></>
                )
              }
            />
            {renderPhoneVerificationError()}
          </FormContentInputFieldWrapperStretch>
        </FormPhoneValidationTextAndInputWrapper>

        <FormCallsToActionWrapper>
          <StylisedButton
            isLoading={isLoadingPhoneNumber}
            isDisabled={isLoadingPhoneNumber || !turnstileToken}
            onClick={() => handleVerifyPhoneNumber()}
            style={StylisedButtonStyle.SALMON}
            buttonText={"Submit"}
          />

          <StylisedButton
            isLoading={false}
            isDisabled={isLoadingPhoneNumber}
            onClick={handlePhoneValidationGoBack}
            style={StylisedButtonStyle.OUTLINE}
            buttonText={"Go back"}
          />
        </FormCallsToActionWrapper>

        <TurnstileWidget />
      </FormContent>
    );
  }

  return (
    <FormContent>
      <FormPhoneValidationTextAndInputWrapper>
        <FormPhoneValidationHeaderText>Verify your phone number</FormPhoneValidationHeaderText>
        <FormContentInputFieldWrapperStretch>
          <PhoneInputField />
          {renderPhoneError()}
        </FormContentInputFieldWrapperStretch>
      </FormPhoneValidationTextAndInputWrapper>

      <FormCallsToActionWrapper>
        <StylisedButton
          isLoading={isLoadingPhoneNumber}
          isDisabled={
            isLoadingPhoneNumber ||
            (countdownSeconds !== null && countdownSeconds > 0) ||
            !turnstileToken
          }
          onClick={() => handleSubmitPhoneNumber()}
          style={StylisedButtonStyle.SALMON}
          buttonText={"Send code"}
        />

        <StylisedButton
          isLoading={false}
          isDisabled={isLoadingPhoneNumber}
          onClick={handlePhoneInputGoBack}
          style={StylisedButtonStyle.OUTLINE}
          buttonText={"Go back"}
        />
      </FormCallsToActionWrapper>
      <TurnstileWidget />
    </FormContent>
  );
};

const FormDisclosureComponent = () => {
  const { handleDisclosureComplete, handleDisclosureClose, signupStateRegion, signupStateNotRequired } =
    useSignUpForm();

  // Lookup can resolve 404 after the stage was entered — proceed instead of showing ToB
  const hasAutoCompleted = React.useRef(false);
  useEffect(() => {
    if (signupStateNotRequired && !hasAutoCompleted.current) {
      hasAutoCompleted.current = true;
      handleDisclosureComplete({});
    }
  }, [signupStateNotRequired, handleDisclosureComplete]);

  if (signupStateNotRequired) return null;

  return (
    <DisclosureFlow
      region={signupStateRegion}
      isOpen={true}
      onClose={handleDisclosureClose}
      onComplete={handleDisclosureComplete}
    />
  );
};

const FormComponent = () => {
  const isMobile = useIsMobile();

  const { signUpStage } = useSignUpFormSlice();

  const Content = () => {
    if (signUpStage === SIGN_UP_STAGE.PHONE) {
      return <FormPhoneValidationComponent />;
    } else if (signUpStage === SIGN_UP_STAGE.SUBSCRIPTION) {
      return <FormSubscriptionComponent />;
    } else if (signUpStage === SIGN_UP_STAGE.UNSUPPORTED_COUNTRY) {
      return <FormUnsupportedCountryComponent />;
    }
    return <FormInitialInputFormComponent />;
  };

  return isMobile ? (
    <FormFrameMobile>
      <Content />
      {signUpStage === SIGN_UP_STAGE.DISCLOSURE && <FormDisclosureComponent />}
    </FormFrameMobile>
  ) : (
    <FormFrame>
      <FormHeaderComponent />
      <Content />
      {signUpStage === SIGN_UP_STAGE.DISCLOSURE && <FormDisclosureComponent />}
    </FormFrame>
  );
};

export function LandingPageForm() {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();

  const queryParams = new URLSearchParams(window.location.search);
  const referrer = queryParams.get("referrer");
  const utm_source = queryParams.get("utm_source");
  const utm_medium = queryParams.get("utm_medium");
  const utm_campaign = queryParams.get("utm_campaign");
  const gclid__c = queryParams.get("gclid__c");
  const signupSources = { referrer, utm_source, utm_medium, utm_campaign, gclid__c };

  useEffect(() => {
    if (signupSources) {
      // Sign up sources are set in the store
      dispatch(setSignupSources(signupSources));
      // Lead initiated sign up event
      trackEvent("Lead Tracked on signup page with sources", { ...signupSources }, "Sign_Up");
    }
  }, []);

  const { signUpStage } = useSignUpFormSlice();
  const isFullScreen = signUpStage === SIGN_UP_STAGE.SUBSCRIPTION;

  // ---------------------- DESIGN ----------------------
  return isMobile ? (
    <>
      <FormHeaderComponent />
      <FormComponent />
    </>
  ) : (
    <FormWrapper item xs={isFullScreen ? 12 : 6}>
      <FormComponent />
    </FormWrapper>
  );
}
